import React, { useState, useMemo, useEffect, useCallback } from 'react';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import PropTypes from 'prop-types';
import {
  Dialog, DialogTitle, DialogContent, Button,
  TextField, Box, Typography, ToggleButtonGroup, ToggleButton, IconButton,
  Divider, CircularProgress, Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ReactECharts from 'echarts-for-react';
import { useAuth } from '../context/AuthContext';

/**
 * ChartComparisonDialog — compare two time periods of the same chart.
 *
 * Props:
 *   open, onClose, barData (full chart data with dates/ohlc/volumes)
 *
 * User picks two start dates (both must exist within barData.dates).
 * Period B = newer date → end of data.  Length = N bars.
 * Period A = older date → older date + N bars.
 * Display modes: "overlay" (same panel, Period A rescaled) or "split" (stacked).
 */

const ChartComparisonDialog = ({ open, onClose, barData, symbol }) => {
  const { api } = useAuth();

  // --- Manual study state ---
  const [dateA, setDateA] = useState('');   // older
  const [dateB, setDateB] = useState('');   // newer
  const [previewBarsInput, setPreviewBarsInput] = useState('20');
  const [appliedDateA, setAppliedDateA] = useState('');
  const [appliedDateB, setAppliedDateB] = useState('');

  // --- Fractal auto-find state (independent) ---
  const [fractalQueryLen, setFractalQueryLen] = useState('100');
  const [fractalTopK, setFractalTopK] = useState('5');
  const [fractalForwardH, setFractalForwardH] = useState('30');
  const [fractalMinCorr, setFractalMinCorr] = useState('0');
  const [fractalLoading, setFractalLoading] = useState(false);
  const [fractalResults, setFractalResults] = useState(null);
  const [fractalError, setFractalError] = useState('');
  const [fractalAppliedA, setFractalAppliedA] = useState('');  // selected match date
  const [fractalAppliedB, setFractalAppliedB] = useState('');  // query start date

  // --- Shared display state ---
  const [activeStudy, setActiveStudy] = useState('manual'); // 'manual' | 'fractal'
  const [mode, setMode] = useState('overlay'); // 'overlay' | 'split'
  const [darkMode, setDarkMode] = useState(false);
  const [error, setError] = useState('');
  const [matchDegree, setMatchDegree] = useState(null);

  const previewBars = Math.max(1, Number.parseInt(previewBarsInput, 10) || 1);

  const hasPendingDateChanges = dateA !== appliedDateA || dateB !== appliedDateB;
  const canRefresh = Boolean(dateA && dateB) && hasPendingDateChanges;

  const handleRefreshChart = () => {
    setAppliedDateA(dateA);
    setAppliedDateB(dateB);
    setActiveStudy('manual');
  };

  // Reset on open
  useEffect(() => {
    if (open) {
      setError('');
      setFractalError('');
    }
  }, [open]);

  // Fractal scan handler (fully independent of manual dates)
  const handleFractalScan = useCallback(async () => {
    if (!symbol) { setFractalError('No symbol loaded.'); return; }
    const qLen = Number.parseInt(fractalQueryLen, 10) || 100;
    const topK = Number.parseInt(fractalTopK, 10) || 5;
    const fwdH = Number.parseInt(fractalForwardH, 10) || 30;
    if (qLen < 50 || qLen > 500) { setFractalError('Query length must be 50–500.'); return; }

    setFractalLoading(true);
    setFractalError('');
    setFractalResults(null);
    const minCorr = (Number.parseFloat(fractalMinCorr) || 0) / 100.0;
    try {
      const res = await api.post('/api/fractal/scan', {
        symbol, queryLength: qLen, topK, forwardH: fwdH,
        minCorrelation: minCorr,
      });
      setFractalResults(res.data);
      // Auto-select first match
      if (res.data?.matches?.length > 0) {
        const firstMatch = res.data.matches[0].startDate;
        const d = firstMatch.substring(0, 10);
        // Determine query start date (last qLen bars of data)
        let qStart = '';
        if (barData?.dates?.length) {
          const startIdx = barData.dates.length - qLen;
          if (startIdx >= 0) qStart = barData.dates[startIdx].substring(0, 10);
        }
        setFractalAppliedA(d);
        setFractalAppliedB(qStart);
        setActiveStudy('fractal');
      }
    } catch (err) {
      setFractalError(err.response?.data?.error || err.message || 'Scan failed');
    } finally {
      setFractalLoading(false);
    }
  }, [api, symbol, fractalQueryLen, fractalTopK, fractalForwardH, fractalMinCorr, barData]);

  // When user clicks a fractal match chip (independent of manual dates)
  const handleSelectMatch = (startDate) => {
    const d = startDate.substring(0, 10);
    // Query start: last qLen bars of data
    let qStart = fractalAppliedB;
    if (!qStart && barData?.dates?.length) {
      const qLen = Number.parseInt(fractalQueryLen, 10) || 100;
      const startIdx = barData.dates.length - qLen;
      if (startIdx >= 0) qStart = barData.dates[startIdx].substring(0, 10);
    }
    setFractalAppliedA(d);
    if (qStart) setFractalAppliedB(qStart);
    setActiveStudy('fractal');
  };

  // Derive available date range for validation
  const dateRange = useMemo(() => {
    if (!barData?.dates?.length) return { min: '', max: '' };
    const first = barData.dates[0].substring(0, 10);
    const last  = barData.dates[barData.dates.length - 1].substring(0, 10);
    return { min: first, max: last };
  }, [barData]);

  // Build comparison data (pure computation — no setState inside useMemo)
  // Picks source dates from manual or fractal study based on activeStudy
  const comparisonResult = useMemo(() => {
    const srcA = activeStudy === 'fractal' ? fractalAppliedA : appliedDateA;
    const srcB = activeStudy === 'fractal' ? fractalAppliedB : appliedDateB;
    if (!srcA || !srcB || !barData?.dates?.length) return { data: null, error: '' };
    const dA = srcA.substring(0, 10);
    const dB = srcB.substring(0, 10);
    if (dA >= dB) {
      return { data: null, error: 'Date A (older) must be before Date B (newer).' };
    }

    // Find indices
    const dates = barData.dates.map(d => d.substring(0, 10));
    let idxA = dates.indexOf(dA);
    let idxB = dates.indexOf(dB);

    // If exact date not found, find closest trading date at or after
    if (idxA === -1) {
      idxA = dates.findIndex(d => d >= dA);
    }
    if (idxB === -1) {
      idxB = dates.findIndex(d => d >= dB);
    }
    if (idxA === -1 || idxB === -1 || idxA >= idxB) {
      return { data: null, error: 'Could not find valid trading dates for the selected range.' };
    }

    // Period B length: fractal study caps to queryLength; manual study extends to end of data
    const maxB = dates.length - idxB;
    const lengthB = (activeStudy === 'fractal')
      ? Math.min(Number.parseInt(fractalQueryLen, 10) || maxB, maxB)
      : maxB;
    // Period A: show Period B length plus preview bars, but don't extend into B's territory
    const PREVIEW_BARS = previewBars;
    const availableA = idxB - idxA;
    const lengthA = Math.min(availableA, lengthB + PREVIEW_BARS);

    if (lengthB < 2 || lengthA < 2) {
      return { data: null, error: 'Not enough bars to compare. Ensure the periods do not overlap and have enough data.' };
    }

    const periodA = {
      dates: dates.slice(idxA, idxA + lengthA),
      ohlc:  barData.ohlc.slice(idxA, idxA + lengthA),
      volumes: barData.volumes.slice(idxA, idxA + lengthA),
    };
    const periodB = {
      dates: dates.slice(idxB, idxB + lengthB),
      ohlc:  barData.ohlc.slice(idxB, idxB + lengthB),
      volumes: barData.volumes.slice(idxB, idxB + lengthB),
    };

    return { data: { periodA, periodB, lengthA, lengthB }, error: '' };
  }, [activeStudy, appliedDateA, appliedDateB, fractalAppliedA, fractalAppliedB, barData, previewBars, fractalQueryLen]);

  const comparison = comparisonResult.data;

  // Sync error state from comparison result
  useEffect(() => {
    setError(comparisonResult.error);
  }, [comparisonResult.error]);

  // Auto-calculate match degree whenever comparison changes
  useEffect(() => {
    if (!comparison) { setMatchDegree(null); return; }
    const { periodA, periodB } = comparison;
    // Use the shorter period (Period B) for correlation — matches what the fractal engine reports
    const minLen = Math.min(periodA.ohlc.length, periodB.ohlc.length);
    if (minLen < 2) { setMatchDegree(null); return; }
    const closesA = periodA.ohlc.slice(0, minLen).map(b => b[1]);
    const closesB = periodB.ohlc.slice(0, minLen).map(b => b[1]);
    const n = minLen;
    let sumA = 0, sumB = 0, sumAB = 0, sumA2 = 0, sumB2 = 0;
    for (let i = 0; i < n; i++) {
      sumA += closesA[i]; sumB += closesB[i];
      sumAB += closesA[i] * closesB[i];
      sumA2 += closesA[i] * closesA[i];
      sumB2 += closesB[i] * closesB[i];
    }
    const num = n * sumAB - sumA * sumB;
    const den = Math.sqrt((n * sumA2 - sumA * sumA) * (n * sumB2 - sumB * sumB));
    const r = den < 1e-10 ? 0 : num / den;
    setMatchDegree(Math.round(r * 10000) / 10000);
  }, [comparison]);

  // Build ECharts options
  const chartOption = useMemo(() => {
    if (!comparison) return null;
    const { periodA, periodB, lengthA, lengthB } = comparison;
    const maxLen = Math.max(lengthA, lengthB);
    const xLabels = Array.from({ length: maxLen }, (_, i) => `Day ${i + 1}`);

    const closesA = periodA.ohlc.map(bar => bar[1]); // close
    const closesB = periodB.ohlc.map(bar => bar[1]);

    // Build tooltip formatter that shows actual dates and OHLC
    const tooltipFormatter = (params) => {
      const dayIdx = params[0]?.dataIndex ?? 0;
      let tip = `<b>Day ${dayIdx + 1}</b><br/>`;
      params.forEach(p => {
        const idx = p.dataIndex;
        const isA = p.seriesName.startsWith('Period A');
        const prd = isA ? periodA : periodB;
        if (idx < prd.dates.length) {
          const bar = prd.ohlc[idx];
          const date = prd.dates[idx];
          tip += `${p.marker} <b>${date}</b> O:${bar[0].toFixed(2)} H:${bar[2].toFixed(2)} L:${bar[3].toFixed(2)} C:${bar[1].toFixed(2)}<br/>`;
        }
      });
      return tip;
    };

    if (mode === 'overlay') {
      // Normalize period A to period B's range for shape comparison
      const minA = Math.min(...closesA), maxA = Math.max(...closesA);
      const minB = Math.min(...closesB), maxB = Math.max(...closesB);
      const rangeA = maxA - minA || 1;
      const rangeB = maxB - minB || 1;
      const normalizedA = closesA.map(v => minB + ((v - minA) / rangeA) * rangeB);

      return {
        tooltip: { trigger: 'axis', formatter: tooltipFormatter },
        backgroundColor: darkMode ? '#1e1e1e' : '#fff',
        legend: { data: [`Period A (${periodA.dates[0]})`, `Period B (${periodB.dates[0]})`], top: 5, textStyle: { color: darkMode ? '#ddd' : '#333' } },
        grid: { left: 60, right: 30, top: 50, bottom: 40 },
        xAxis: { type: 'category', data: xLabels, axisLabel: { color: darkMode ? '#aaa' : '#333', interval: Math.floor(maxLen / 20) }, axisLine: { lineStyle: { color: darkMode ? '#555' : '#ccc' } } },
        yAxis: { type: 'value', scale: true, axisLabel: { color: darkMode ? '#aaa' : '#333' }, splitLine: { lineStyle: { color: darkMode ? '#333' : '#eee' } } },
        series: [
          {
            name: `Period A (${periodA.dates[0]})`,
            type: 'line',
            data: normalizedA,
            lineStyle: { color: '#1976d2', width: 2 },
            itemStyle: { color: '#1976d2' },
            showSymbol: false,
            smooth: false,
            z: 10,
          },
          {
            name: `Period B (${periodB.dates[0]})`,
            type: 'line',
            data: closesB,
            lineStyle: { color: '#e53935', width: 2 },
            itemStyle: { color: '#e53935' },
            showSymbol: false,
            smooth: false,
            z: 11,
          },
        ],
        dataZoom: [
          { type: 'inside', start: 0, end: 100 },
          { type: 'slider', start: 0, end: 100, height: 20, bottom: 5 },
        ],
      };
    }

    // Split mode: two grids stacked
    return {
      backgroundColor: darkMode ? '#1e1e1e' : '#fff',
      tooltip: { trigger: 'axis', formatter: tooltipFormatter },
      legend: { data: [`Period B (${periodB.dates[0]})`, `Period A (${periodA.dates[0]})`], top: 5, textStyle: { color: darkMode ? '#ddd' : '#333' } },
      axisPointer: { link: [{ xAxisIndex: [0, 1] }] },
      grid: [
        { left: 60, right: 30, top: 50, height: '35%' },
        { left: 60, right: 30, top: '58%', height: '35%' },
      ],
      xAxis: [
        { type: 'category', data: xLabels, gridIndex: 0, axisLabel: { show: false }, axisLine: { lineStyle: { color: darkMode ? '#555' : '#ccc' } } },
        { type: 'category', data: xLabels, gridIndex: 1, axisLabel: { color: darkMode ? '#aaa' : '#333', interval: Math.floor(maxLen / 20) }, axisLine: { lineStyle: { color: darkMode ? '#555' : '#ccc' } } },
      ],
      yAxis: [
        { type: 'value', scale: true, gridIndex: 0, name: `Period B (${periodB.dates[0]})`, nameTextStyle: { fontSize: 11, color: darkMode ? '#ddd' : '#333' }, axisLabel: { color: darkMode ? '#aaa' : '#333' }, splitLine: { lineStyle: { color: darkMode ? '#333' : '#eee' } } },
        { type: 'value', scale: true, gridIndex: 1, name: `Period A (${periodA.dates[0]})`, nameTextStyle: { fontSize: 11, color: darkMode ? '#ddd' : '#333' }, axisLabel: { color: darkMode ? '#aaa' : '#333' }, splitLine: { lineStyle: { color: darkMode ? '#333' : '#eee' } } },
      ],
      series: [
        {
          name: `Period B (${periodB.dates[0]})`,
          type: 'candlestick',
          data: periodB.ohlc,
          xAxisIndex: 0,
          yAxisIndex: 0,
          itemStyle: { color: '#26a69a', color0: '#ef5350', borderColor: '#26a69a', borderColor0: '#ef5350' },
        },
        {
          name: `Period A (${periodA.dates[0]})`,
          type: 'candlestick',
          data: periodA.ohlc,
          xAxisIndex: 1,
          yAxisIndex: 1,
          itemStyle: { color: '#1976d2', color0: '#ff9800', borderColor: '#1976d2', borderColor0: '#ff9800' },
        },
      ],
      dataZoom: [
        { type: 'inside', xAxisIndex: [0, 1], start: 0, end: 100 },
        { type: 'slider', xAxisIndex: [0, 1], start: 0, end: 100, height: 20, bottom: 5 },
      ],
    };
  }, [comparison, mode, darkMode]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{ sx: { maxWidth: 2400, width: '98vw', height: '85vh', maxHeight: '85vh', bgcolor: darkMode ? '#121212' : '#fff', color: darkMode ? '#eee' : 'inherit' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 1, position: 'relative' }}>
        <Typography variant="h6" fontWeight="bold" sx={{ textAlign: 'center', flex: 1 }}>Compare Two Time Periods</Typography>
        <IconButton onClick={onClose} size="small" sx={{ position: 'absolute', right: 8 }}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        {/* Controls row */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: darkMode ? '#d1d5db' : '#374151',
                letterSpacing: 0.2,
              }}
            >
              Period A Start (older)
            </Typography>
            <TextField
              type="date"
              size="small"
              value={dateA}
              onChange={e => setDateA(e.target.value)}
              inputProps={{ min: dateRange.min, max: dateRange.max }}
              sx={{
                width: 210,
                '& .MuiInputBase-input': { color: darkMode ? '#eee' : '#111827' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: darkMode ? '#555' : '#cbd5e1' },
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: darkMode ? '#d1d5db' : '#374151',
                letterSpacing: 0.2,
              }}
            >
              Period B Start (newer)
            </Typography>
            <TextField
              type="date"
              size="small"
              value={dateB}
              onChange={e => setDateB(e.target.value)}
              inputProps={{ min: dateRange.min, max: dateRange.max }}
              sx={{
                width: 210,
                '& .MuiInputBase-input': { color: darkMode ? '#eee' : '#111827' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: darkMode ? '#555' : '#cbd5e1' },
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: darkMode ? '#d1d5db' : '#374151',
                letterSpacing: 0.2,
              }}
            >
              Preview Bars (positive)
            </Typography>
            <TextField
              type="number"
              size="small"
              value={previewBarsInput}
              onChange={(e) => {
                const next = e.target.value;
                if (/^\d*$/.test(next)) {
                  setPreviewBarsInput(next === '' ? '' : String(Math.max(1, Number.parseInt(next, 10) || 1)));
                }
              }}
              inputProps={{ min: 1, step: 1 }}
              sx={{
                width: 160,
                '& .MuiInputBase-input': { color: darkMode ? '#eee' : '#111827' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: darkMode ? '#555' : '#cbd5e1' },
              }}
            />
          </Box>
          <Button
            variant="contained"
            size="small"
            onClick={handleRefreshChart}
            disabled={!canRefresh}
            sx={{ height: 40, textTransform: 'none' }}
          >
            Refresh Chart
          </Button>
          <ToggleButtonGroup
            value={mode}
            exclusive
            onChange={(_, v) => { if (v) setMode(v); }}
            size="small"
          >
            <ToggleButton value="overlay" sx={{ color: darkMode ? '#ccc' : undefined }}>Overlay</ToggleButton>
            <ToggleButton value="split" sx={{ color: darkMode ? '#ccc' : undefined }}>Split</ToggleButton>
          </ToggleButtonGroup>
          <IconButton onClick={() => setDarkMode(d => !d)} size="small" sx={{ ml: 1 }}>
            {darkMode ? <LightModeIcon sx={{ color: '#fbc02d' }} /> : <DarkModeIcon sx={{ color: '#555' }} />}
          </IconButton>
          {matchDegree !== null && (
            <Typography variant="body2" sx={{ fontWeight: 700, color: matchDegree > 0.7 ? '#2e7d32' : matchDegree > 0.4 ? '#f57c00' : '#d32f2f' }}>
              Match: {(matchDegree * 100).toFixed(1)}% ({matchDegree > 0.8 ? 'Strong' : matchDegree > 0.6 ? 'Moderate' : matchDegree > 0.3 ? 'Weak' : 'Very Weak'})
            </Typography>
          )}
          {comparison && (
            <Typography variant="body2" color="text.secondary">
              Period A: {comparison.lengthA} bars | Period B: {comparison.lengthB} bars
              {comparison.lengthA > comparison.lengthB ? ` (+${comparison.lengthA - comparison.lengthB} preview)` : ''}
            </Typography>
          )}
        </Box>

        {error && (
          <Typography color="error" variant="body2">{error}</Typography>
        )}

        {/* Fractal Auto-Find Section */}
        <Divider sx={{ borderColor: darkMode ? '#444' : '#e0e0e0' }} />
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: darkMode ? '#90caf9' : '#1565c0' }}>
            Auto-Find Best Match
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: darkMode ? '#d1d5db' : '#374151' }}>Query Bars (50–500)</Typography>
            <TextField type="number" size="small" value={fractalQueryLen}
              onChange={e => setFractalQueryLen(e.target.value)}
              inputProps={{ min: 50, max: 500, step: 10 }}
              sx={{ width: 120, '& .MuiInputBase-input': { color: darkMode ? '#eee' : '#111827' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: darkMode ? '#555' : '#cbd5e1' } }}
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: darkMode ? '#d1d5db' : '#374151' }}>Top K</Typography>
            <TextField type="number" size="small" value={fractalTopK}
              onChange={e => setFractalTopK(e.target.value)}
              inputProps={{ min: 1, max: 20, step: 1 }}
              sx={{ width: 80, '& .MuiInputBase-input': { color: darkMode ? '#eee' : '#111827' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: darkMode ? '#555' : '#cbd5e1' } }}
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: darkMode ? '#d1d5db' : '#374151' }}>Forward Bars</Typography>
            <TextField type="number" size="small" value={fractalForwardH}
              onChange={e => setFractalForwardH(e.target.value)}
              inputProps={{ min: 5, max: 100, step: 5 }}
              sx={{ width: 100, '& .MuiInputBase-input': { color: darkMode ? '#eee' : '#111827' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: darkMode ? '#555' : '#cbd5e1' } }}
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: darkMode ? '#d1d5db' : '#374151' }}>Min Corr. %</Typography>
            <TextField type="number" size="small" value={fractalMinCorr}
              onChange={e => setFractalMinCorr(e.target.value)}
              inputProps={{ min: 0, max: 99, step: 5 }}
              sx={{ width: 100, '& .MuiInputBase-input': { color: darkMode ? '#eee' : '#111827' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: darkMode ? '#555' : '#cbd5e1' } }}
            />
          </Box>
          <Button
            variant="contained"
            size="small"
            onClick={handleFractalScan}
            disabled={fractalLoading}
            sx={{ height: 40, textTransform: 'none', bgcolor: '#1565c0' }}
          >
            {fractalLoading ? <CircularProgress size={20} color="inherit" /> : 'Find Matches'}
          </Button>
          {fractalResults && (
            <Typography variant="caption" sx={{ color: darkMode ? '#aaa' : '#666' }}>
              {fractalResults.elapsedMs}ms | {fractalResults.matches?.length} matches
            </Typography>
          )}
        </Box>
        {fractalError && <Typography color="error" variant="body2">{fractalError}</Typography>}
        {fractalResults?.matches?.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {fractalResults.matches.map((m, idx) => (
              <Chip
                key={idx}
                label={`#${idx + 1} ${m.startDate?.substring(0, 10)} | score=${m.compositeScore} r=${m.correlation} [${m.method}]`}
                onClick={() => handleSelectMatch(m.startDate)}
                variant={fractalAppliedA === m.startDate?.substring(0, 10) ? 'filled' : 'outlined'}
                color={fractalAppliedA === m.startDate?.substring(0, 10) ? 'primary' : 'default'}
                sx={{ cursor: 'pointer', color: darkMode ? '#eee' : undefined }}
              />
            ))}
          </Box>
        )}

        {/* Chart */}
        {chartOption ? (
          <Box sx={{ flex: 1, minHeight: 300 }}>
            <ReactECharts
              key={`${activeStudy}-${activeStudy === 'fractal' ? fractalAppliedA : appliedDateA}-${activeStudy === 'fractal' ? fractalAppliedB : appliedDateB}`}
              option={chartOption}
              style={{ width: '100%', height: '100%' }}
              notMerge
            />
          </Box>
        ) : (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography color="text.secondary">
              {fractalResults?.matches?.length > 0
                ? 'Click a match chip above to view the comparison.'
                : dateA && dateB && hasPendingDateChanges
                  ? 'Click "Refresh Chart" to apply the selected dates.'
                  : 'Use "Find Matches" above, or manually set Period A & B dates to compare.'}
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

ChartComparisonDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  barData: PropTypes.shape({
    dates: PropTypes.array,
    ohlc: PropTypes.array,
    volumes: PropTypes.array,
  }),
  symbol: PropTypes.string,
};

export default ChartComparisonDialog;
